/* First-run spotlight tour — a few steps per page, shown once, dismissible.

   Usage: <SpotlightTour tourId="dashboard" steps=[{ target: '[data-tour=doc-tree]',
   title, body }] />. Targets are data-tour attributes so steps survive
   refactors. A step whose target isn't on screen is skipped. Seen-state in
   localStorage (fl.tour.<id>) — per browser, matching the app's other UI
   prefs. */
import React, { useEffect, useMemo, useState } from 'react';
import './SpotlightTour.css';

const seenKey = (id) => `fl.tour.${id}`;

export default function SpotlightTour({ tourId, steps, delay = 900 }) {
  const [idx, setIdx] = useState(-1); // -1 = not started/finished
  const [rect, setRect] = useState(null);

  // Only offer the tour once per browser.
  const eligible = useMemo(() => {
    try { return localStorage.getItem(seenKey(tourId)) !== '1'; } catch { return false; }
  }, [tourId]);

  useEffect(() => {
    if (!eligible) return undefined;
    const t = setTimeout(() => setIdx(0), delay); // let the page settle first
    return () => clearTimeout(t);
  }, [eligible, delay]);

  const finish = () => {
    try { localStorage.setItem(seenKey(tourId), '1'); } catch { /* private mode */ }
    setIdx(-1);
  };

  // Measure the current step's target; skip steps whose target isn't there.
  useEffect(() => {
    if (idx < 0) return undefined;
    if (idx >= steps.length) { finish(); return undefined; }
    const el = document.querySelector(steps[idx].target);
    if (!el) { setIdx((i) => i + 1); return undefined; }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    let raf;
    const measure = () => { setRect(el.getBoundingClientRect()); raf = requestAnimationFrame(measure); };
    raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, steps]);

  if (idx < 0 || idx >= steps.length || !rect) return null;
  const step = steps[idx];
  const pad = 8;
  const below = rect.bottom + 180 < window.innerHeight;

  return (
    <div className="st-scrim" onClick={finish}>
      <div
        className="st-ring"
        style={{
          top: rect.top - pad,
          left: rect.left - pad,
          width: rect.width + pad * 2,
          height: rect.height + pad * 2,
        }}
      />
      <div
        className="st-tip"
        onClick={(e) => e.stopPropagation()}
        style={{
          top: below ? rect.bottom + pad + 10 : undefined,
          bottom: below ? undefined : window.innerHeight - rect.top + pad + 10,
          left: Math.max(12, Math.min(rect.left, window.innerWidth - 332)),
        }}
      >
        <div className="st-tip-title">{step.title}</div>
        <div className="st-tip-body">{step.body}</div>
        <div className="st-tip-row">
          <span className="st-tip-count">{idx + 1} / {steps.length}</span>
          <button type="button" className="st-btn st-btn-ghost" onClick={finish}>Skip</button>
          <button type="button" className="st-btn" onClick={() => setIdx((i) => i + 1)}>
            {idx + 1 === steps.length ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
